import {Dispatch, FunctionComponent, SetStateAction, useCallback, useState} from 'react';

import { useFilterMenu } from '../../../../utils/Filter';
import FilterPageLeft from '../../partials/filter/FilterPageLeft';
import FilterPageFilter from '../../partials/filter/FilterPageFilter';
import FilterPageRight from '../../partials/filter/FilterPageRight';
import FilterPageHeader from '../../partials/filter/FilterPageHeader';
import FilterPageContent from '../../partials/filter/FilterPageContent';
import FilterPageFooter from '../../partials/filter/FilterPageFooter';
import {
    FilterParams,
    Filters,
} from '../../../../networking/repos/SubjectAreaRepository';
import SubjectAreaFilter, { FilterChangeEvent } from '../../partials/subject-area/SubjectAreaFilter';
import SubjectAreasTable from '../../partials/subject-area/SubjectAreasTable';
import FilterPage from '../../partials/filter/FilterPage';
import HanButton from '../../../utils/HanButton';
import Breadcrumbs from '../../../utils/Breadcrumbs';
import {SubjectAreaPaginatedResponse} from '../../../../networking/responses/SubjectArea';

export interface SubjectAreasFilterParams extends FilterParams {
    activeFilters?: number;
}

interface Props {
    subjectAreas: SubjectAreaPaginatedResponse;
    filterOptions: Filters;

    filters: SubjectAreasFilterParams;
    setFilters: Dispatch<SetStateAction<SubjectAreasFilterParams>>;

    onPageChange: (page: number) => void;
}

const SubjectAreasContent: FunctionComponent<Props> = (props) => {
    const [filterMenuOpen, toggleFilterMenu] = useFilterMenu();

    const [selectedSubjectAreas, setSelectedSubjectAreas] = useState<string[]>([]);

    const getFilterButtonText = () => {
        if(props.filters.activeFilters && props.filters.activeFilters > 0) {
            return props.filters?.activeFilters?.toString();
        }

        return '+';
    };

    const onFilter = useCallback(({ languages, levels, subjectAreas }: FilterChangeEvent) => {
        setSelectedSubjectAreas(subjectAreas);
        
        props.setFilters(f => ({
          ...f,
          languages,
          levels,
          subject_areas: subjectAreas,
          activeFilters: languages.length + levels.length
        }));
    }, []);

    const onSearch = useCallback((value: string) => {
        props.setFilters(f => ({ ...f, search: value }));
    }, []);

    const onOrderBy = useCallback((column: string, direction?: string) => {
        return props.setFilters(f => ({ ...f, order_by: column, direction }));
    }, []);

    return (
        <FilterPage className={'subject-areas-page'}>
            <FilterPageLeft menuOpen={filterMenuOpen}>
                <FilterPageFilter toggleMenu={toggleFilterMenu} activeFilters={props.filters.activeFilters}>
                    <SubjectAreaFilter
                        subjectAreas={props.filterOptions.subject_areas}
                        levels={props.filterOptions.levels}
                        languages={props.filterOptions.languages}
                        onFilterChange={onFilter} />

                    <div className={'filter-page__filter-button'}>
                        <HanButton onClick={toggleFilterMenu}>Show {props.subjectAreas.total} results</HanButton>
                    </div>
                </FilterPageFilter>
            </FilterPageLeft>

            <FilterPageRight>
                <FilterPageHeader
                    title={selectedSubjectAreas.length <= 1 ? selectedSubjectAreas[0] || 'None selected' :  `${selectedSubjectAreas.length} selected`}
                    searchQuery={props.filters.search ?? ''}
                    onSearch={onSearch}>
                </FilterPageHeader>

                <FilterPageContent>
                    <div className={'subject-areas-page__subjects'}>
                        <Breadcrumbs
                            breadcrumbs={
                                [
                                    { name: "Home", url: "/" },
                                    { name: "Subject areas", url: "/subject-areas" }
                                ]
                            }
                        />

                        {/* br-tag is used because FilterPageRight is used on multiple pages with different styling. 
                        Applying style would change it everywhere. This br-tag does not*/}
                        <br></br>

                        <span className={'subject-areas-page__text'}>Click on a column to sort on alphabetical order</span>

                        <SubjectAreasTable
                            searchQuery={props.filters.search}
                            subjectAreas={props.subjectAreas.data}
                            onOrderBy={onOrderBy} />
                    </div>
                </FilterPageContent>

                <FilterPageFooter
                    total={props.subjectAreas.total}
                    pageCount={props.subjectAreas.lastPage}
                    currentPage={props.subjectAreas.currentPage}
                    onPageChange={props.onPageChange}>

                    <HanButton onClick={toggleFilterMenu} modifiers={['universities-page__button--big']} indicatorText={getFilterButtonText()}>Filter programs</HanButton>
                </FilterPageFooter>
            </FilterPageRight>
        </FilterPage>
    );
};

export default SubjectAreasContent;
